import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  Radio, Users, Heart, Send, Flame, Gift, 
  Video, VideoOff, Mic, MicOff, Settings,
  Share2, MoreVertical, X, Play, Eye,
  MessageCircle, ChevronUp, ChevronDown, Shield, Flag, Trash2, Clock, Ban
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import StreamModerationPanel, { ChatMessageActions } from '../components/moderation/StreamModerationPanel';
import ReportDialog from '../components/moderation/ReportDialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LiveStreamPage() {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token, user } = useAuth();
  
  // State
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [viewerCount, setViewerCount] = useState(0);
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [donationAmount, setDonationAmount] = useState(10);
  const [donationMessage, setDonationMessage] = useState('');
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [likeAnimations, setLikeAnimations] = useState([]);
  const [showModerationPanel, setShowModerationPanel] = useState(false);
  const [moderationStatus, setModerationStatus] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  
  // Refs
  const chatEndRef = useRef(null);
  const videoRef = useRef(null);
  const pollingRef = useRef(null);
  
  const isCreator = stream?.creator_id === user?.id;
  const canModerate = isCreator || moderationStatus?.is_moderator;
  
  // Fetch moderation status
  const fetchModerationStatus = useCallback(async () => {
    if (!token || !streamId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/moderation/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setModerationStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch moderation status:', err);
    }
  }, [token, streamId]);
  
  // Fetch stream data
  const fetchStream = useCallback(async () => {
    if (!streamId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}`);
      
      if (res.ok) {
        const data = await res.json();
        setStream(data);
        setViewerCount(data.viewer_count || 0);
        setLikes(data.likes_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch stream:', err);
    } finally {
      setLoading(false);
    }
  }, [streamId]);
  
  // Fetch chat messages
  const fetchMessages = useCallback(async () => {
    if (!streamId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/chat`);
      
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  }, [streamId]);
  
  // Join stream
  const joinStream = useCallback(async () => {
    if (!token || !streamId) return;
    
    try {
      await fetch(`${API_URL}/api/streams/${streamId}/join`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to join stream:', err);
    }
  }, [token, streamId]);
  
  // Leave stream
  const leaveStream = useCallback(async () => {
    if (!token || !streamId) return;
    
    try {
      await fetch(`${API_URL}/api/streams/${streamId}/leave`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to leave stream:', err);
    }
  }, [token, streamId]);
  
  // Initial load
  useEffect(() => {
    fetchStream();
    fetchMessages();
    
    if (token) {
      joinStream();
      fetchModerationStatus();
    }
    
    // Polling
    pollingRef.current = setInterval(() => {
      fetchStream();
      fetchMessages();
    }, 2000);
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (token) {
        leaveStream();
      }
    };
  }, [fetchStream, fetchMessages, joinStream, leaveStream, fetchModerationStatus, token]);
  
  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Send message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!newMessage.trim() || !token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: newMessage })
      });
      
      if (res.ok) {
        setNewMessage('');
        fetchMessages();
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };
  
  // Toggle like
  const handleLike = async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/like`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setHasLiked(data.liked);
        setLikes(prev => data.liked ? prev + 1 : prev - 1);
        
        // Like animation
        if (data.liked) {
          const id = Date.now();
          setLikeAnimations(prev => [...prev, id]);
          setTimeout(() => {
            setLikeAnimations(prev => prev.filter(a => a !== id));
          }, 2000);
        }
      }
    } catch (err) {
      console.error('Failed to like:', err);
    }
  };
  
  // Send donation
  const handleDonation = async () => {
    if (!token || donationAmount < 1) return;
    
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/donate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: donationAmount,
          message: donationMessage || null
        })
      });
      
      if (res.ok) {
        setShowDonationModal(false);
        setDonationAmount(10);
        setDonationMessage('');
        fetchMessages();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to donate');
      }
    } catch (err) {
      console.error('Failed to donate:', err);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="animate-spin h-12 w-12 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!stream) {
    return (
      <div className="flex items-center justify-center h-screen bg-black text-white">
        <div className="text-center">
          <Video className="h-16 w-16 mx-auto mb-4 text-gray-600" />
          <h2 className="text-xl font-semibold mb-2">Stream non trouvé</h2>
          <Button onClick={() => navigate('/live')} variant="outline">
            Voir les lives
          </Button>
        </div>
      </div>
    );
  }
  
  const isLive = stream.status === 'live';
  
  return (
    <div className="flex h-screen bg-black" data-testid="live-stream-page">
      {/* Main Video Area */}
      <div className="flex-1 flex flex-col relative">
        {/* Video */}
        <div className="flex-1 relative bg-gradient-to-b from-gray-900 to-black flex items-center justify-center">
          {isLive ? (
            <>
              {/* Placeholder for actual video stream */}
              <div className="text-white text-center">
                <Radio className="h-16 w-16 mx-auto mb-4 text-red-500 animate-pulse" />
                <p className="text-xl font-bold">{stream.title}</p>
                <p className="text-gray-400">{stream.description}</p>
              </div>
              
              {/* Live badge */}
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <Badge className="bg-red-500 text-white flex items-center gap-1 px-3 py-1">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  LIVE
                </Badge>
                <Badge variant="outline" className="text-white border-white/30">
                  <Eye className="h-3 w-3 mr-1" />
                  {viewerCount}
                </Badge>
              </div>
              
              {/* Like animations */}
              {likeAnimations.map((id) => (
                <div
                  key={id}
                  className="absolute bottom-20 right-20 animate-bounce"
                  style={{
                    animation: 'float-up 2s ease-out forwards'
                  }}
                >
                  <Heart className="h-8 w-8 text-red-500 fill-red-500" />
                </div>
              ))}
            </>
          ) : stream.status === 'ended' ? (
            <div className="text-white text-center">
              <Video className="h-16 w-16 mx-auto mb-4 text-gray-600" />
              <p className="text-xl font-bold">Stream terminé</p>
              <p className="text-gray-400">
                Durée: {Math.floor(stream.duration_seconds / 60)} minutes
              </p>
            </div>
          ) : (
            <div className="text-white text-center">
              <Video className="h-16 w-16 mx-auto mb-4 text-gray-600" />
              <p className="text-xl font-bold">Stream programmé</p>
              <p className="text-gray-400">{stream.scheduled_start || 'Bientôt'}</p>
            </div>
          )}
        </div>
        
        {/* Bottom Bar */}
        <div className="bg-gray-900 p-4">
          <div className="flex items-center justify-between">
            {/* Creator info */}
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 border-2 border-orange-500">
                <AvatarImage src={stream.creator?.profile_picture} />
                <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500">
                  {stream.creator?.display_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-white">{stream.creator?.display_name}</p>
                <p className="text-xs text-gray-400">@{stream.creator?.username}</p>
              </div>
              <Button variant="outline" size="sm" className="ml-4 border-orange-500 text-orange-500 hover:bg-orange-500/10">
                Suivre
              </Button>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Moderation button (for creator) */}
              {isCreator && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowModerationPanel(true)}
                  className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                  data-testid="moderation-btn"
                >
                  <Shield className="h-5 w-5 mr-1" />
                  Modération
                </Button>
              )}
              
              {/* Like button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLike}
                className={`${hasLiked ? 'text-red-500' : 'text-white'} hover:bg-white/10`}
                disabled={!isLive}
              >
                <Heart className={`h-5 w-5 mr-1 ${hasLiked ? 'fill-red-500' : ''}`} />
                {likes}
              </Button>
              
              {/* Donate button */}
              <Button
                onClick={() => setShowDonationModal(true)}
                className="bg-gradient-to-r from-orange-500 to-red-500"
                disabled={!isLive || isCreator}
              >
                <Gift className="h-4 w-4 mr-2" />
                Donner BARKA
              </Button>
              
              {/* Share */}
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
                <Share2 className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Chat Sidebar */}
      <div className={`${chatCollapsed ? 'w-12' : 'w-80'} bg-gray-900 border-l border-gray-800 flex flex-col transition-all`}>
        {/* Chat header */}
        <div className="p-3 border-b border-gray-800 flex items-center justify-between">
          {!chatCollapsed && (
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-orange-500" />
              <span className="font-semibold text-white">Chat</span>
              {moderationStatus?.is_moderator && (
                <Badge className="bg-green-500 text-[10px]">MOD</Badge>
              )}
            </div>
          )}
          <div className="flex items-center gap-1">
            {canModerate && !chatCollapsed && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowModerationPanel(true)}
                className="text-orange-400 hover:text-orange-300 h-8 w-8"
              >
                <Shield className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setChatCollapsed(!chatCollapsed)}
              className="text-gray-400 hover:text-white"
            >
              {chatCollapsed ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        
        {!chatCollapsed && (
          <>
            {/* Messages */}
            <ScrollArea className="flex-1 p-3">
              <div className="space-y-3">
                {messages.filter(msg => !msg.is_deleted).map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`group ${
                      msg.message_type === 'donation' 
                        ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 p-2 rounded-lg' 
                        : msg.message_type === 'system'
                        ? 'text-center'
                        : ''
                    }`}
                  >
                    {msg.message_type === 'system' ? (
                      <span className="text-xs text-gray-500">{msg.content}</span>
                    ) : (
                      <div className="flex items-start gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={msg.user?.profile_picture} />
                          <AvatarFallback className="text-xs bg-gray-700">
                            {msg.user?.display_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-medium text-orange-400">
                              {msg.user?.display_name}
                            </span>
                            {msg.user?.id === stream?.creator_id && (
                              <Badge className="text-[10px] px-1 py-0 bg-red-500">HOST</Badge>
                            )}
                            {moderationStatus?.is_moderator && msg.user?.id === user?.id && (
                              <Badge className="text-[10px] px-1 py-0 bg-green-500">MOD</Badge>
                            )}
                          </div>
                          {msg.message_type === 'donation' && (
                            <span className="ml-1 text-xs text-yellow-400">
                              <Flame className="h-3 w-3 inline" /> {msg.donation_amount}
                            </span>
                          )}
                          <p className="text-sm text-white break-words">{msg.content}</p>
                        </div>
                        {/* Moderation actions */}
                        {canModerate && msg.user?.id !== user?.id && msg.message_type !== 'system' && (
                          <ChatMessageActions
                            streamId={streamId}
                            messageId={msg.id}
                            userId={msg.user?.id}
                            username={msg.user?.display_name}
                            canModerate={canModerate}
                            isCreator={isCreator}
                            onAction={() => fetchMessages()}
                          />
                        )}
                        {/* Report button for non-mods */}
                        {!canModerate && msg.user?.id !== user?.id && msg.message_type !== 'system' && token && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100"
                            onClick={() => setReportTarget({ type: 'stream_message', id: msg.id, name: msg.content.slice(0, 50) })}
                          >
                            <Flag className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
            </ScrollArea>
            
            {/* Chat input */}
            {isLive && token && moderationStatus?.can_chat && (
              <form onSubmit={handleSendMessage} className="p-3 border-t border-gray-800">
                <div className="flex gap-2">
                  <Input
                    placeholder="Envoyer un message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white text-sm"
                  />
                  <Button 
                    type="submit" 
                    size="icon"
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            )}
            
            {/* Timeout/Ban notice */}
            {isLive && token && moderationStatus && !moderationStatus.can_chat && (
              <div className="p-3 border-t border-gray-800">
                {moderationStatus.is_banned ? (
                  <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 text-center">
                    <Ban className="h-5 w-5 mx-auto text-red-400 mb-1" />
                    <p className="text-sm text-red-300">Tu es banni de ce stream</p>
                  </div>
                ) : moderationStatus.is_timed_out ? (
                  <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3 text-center">
                    <Clock className="h-5 w-5 mx-auto text-yellow-400 mb-1" />
                    <p className="text-sm text-yellow-300">Tu es en timeout</p>
                    {moderationStatus.timeout_info?.expires_at && (
                      <p className="text-xs text-yellow-400 mt-1">
                        Expire: {new Date(moderationStatus.timeout_info.expires_at).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            )}
            
            {/* Login prompt */}
            {isLive && !token && (
              <div className="p-3 border-t border-gray-800 text-center">
                <p className="text-sm text-gray-400">Connecte-toi pour participer au chat</p>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Donation Modal */}
      <Dialog open={showDonationModal} onOpenChange={setShowDonationModal}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-500" />
              Donner des BARKA
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Supportez {stream.creator?.display_name} avec des BARKA!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Quick amounts */}
            <div className="flex gap-2 flex-wrap">
              {[5, 10, 25, 50, 100].map((amount) => (
                <Button
                  key={amount}
                  variant={donationAmount === amount ? 'default' : 'outline'}
                  onClick={() => setDonationAmount(amount)}
                  className={donationAmount === amount 
                    ? 'bg-orange-500' 
                    : 'border-gray-700 text-white hover:bg-gray-800'}
                >
                  {amount} 🔥
                </Button>
              ))}
            </div>
            
            {/* Custom amount */}
            <Input
              type="number"
              min={1}
              value={donationAmount}
              onChange={(e) => setDonationAmount(Number(e.target.value))}
              className="bg-gray-800 border-gray-700"
            />
            
            {/* Message */}
            <Input
              placeholder="Ajouter un message (optionnel)"
              value={donationMessage}
              onChange={(e) => setDonationMessage(e.target.value)}
              className="bg-gray-800 border-gray-700"
            />
          </div>
          
          <DialogFooter>
            <Button
              onClick={handleDonation}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500"
            >
              Donner {donationAmount} BARKA 🔥
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* CSS for like animation */}
      <style>{`
        @keyframes float-up {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-100px) scale(1.5);
          }
        }
      `}</style>
      
      {/* Moderation Panel */}
      {showModerationPanel && (
        <StreamModerationPanel
          streamId={streamId}
          isCreator={isCreator}
          onClose={() => setShowModerationPanel(false)}
        />
      )}
      
      {/* Report Dialog */}
      {reportTarget && (
        <ReportDialog
          open={!!reportTarget}
          onOpenChange={(open) => !open && setReportTarget(null)}
          targetType={reportTarget.type}
          targetId={reportTarget.id}
          targetName={reportTarget.name}
        />
      )}
    </div>
  );
}
